import { lazy, type ComponentType } from 'react';

/**
 * True if the error looks like a dynamic-import / chunk-load failure
 * (stale hash after redeploy, transient network blip, CDN miss, etc.).
 */
export const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false;
  const message =
    (err as { message?: string })?.message ??
    (typeof err === 'string' ? err : '');
  const name = (err as { name?: string })?.name ?? '';
  return (
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(name)
  );
};

/**
 * Wraps React.lazy with two layers of resilience:
 *  1. **In-flight retry** — if the dynamic import fails (transient network
 *     blip, CDN miss), we wait 600ms and try once more before giving up.
 *  2. **Stale-deploy reload** — if it still fails AND it looks like a chunk
 *     load error, we reload the page once per 10s window so the browser
 *     fetches the new index.html and the new chunk hashes.
 *
 * Any error that escapes is re-thrown so a surrounding error boundary
 * (e.g. ChunkErrorBoundary) can render a user-friendly retry page.
 */
export const lazyWithRetry = <T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>
) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (first) {
      // 1) One transparent in-flight retry after a short backoff.
      try {
        await new Promise((r) => setTimeout(r, 600));
        return await factory();
      } catch (err) {
        // 2) If this is the stale-hash case, do a hard reload (max once / 10s)
        //    so the user picks up the new deploy.
        if (typeof window !== 'undefined' && isChunkLoadError(err)) {
          const last = Number(sessionStorage.getItem('chunk-reload-at') || '0');
          if (Date.now() - last >= 10_000) {
            sessionStorage.setItem('chunk-reload-at', String(Date.now()));
            window.location.reload();
          }
        }
        throw err;
      }
    }
  });
